<template>
  <Modal
    name="UPDATE_COMPANY_CARD_MODAL"
    :title="$t('myPlan.card.title')"
    :width="560"
    actions
    persistent
  >
    <template v-slot:body>
      <p class="body-2 grey--text mb-6">{{ $t('myPlan.card.description') }}</p>

      <v-alert v-if="errorMessage" class="body-2 mb-6" type="error" dense outlined text>
        {{ errorMessage }}
      </v-alert>

      <v-form ref="cardForm" lazy-validation>
        <v-text-field
          v-model="holderName"
          :rules="[rules.name]"
          :label="$t('forms.nameAsCreditCard')"
          class="mb-2"
          hide-details="auto"
          type="text"
          outlined
        />

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="cardNumber"
              v-mask="'#### #### #### ####'"
              :rules="[rules.number]"
              :label="$t('forms.creditCardNumber')"
              hide-details="auto"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="8" md="4">
            <v-text-field
              v-model="expiry"
              v-mask="'##/####'"
              :rules="[rules.expiry, rules.expiredMonthValidation, rules.expiredYearValidation]"
              :label="$t('forms.cardDate')"
              :hint="$t('onboarding.card.dueDateHint')"
              hide-details="auto"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="4" md="2">
            <v-text-field
              v-model="ccv"
              v-mask="'####'"
              :rules="[rules.ccv]"
              :label="$t('forms.cardCvv')"
              hide-details="auto"
              type="tel"
              outlined
            />
          </v-col>
        </v-row>
      </v-form>
    </template>

    <template v-slot:actions>
      <v-btn @click="close" :disabled="isSubmitting" class="mr-5" elevation="0" large text>
        {{ $t('myPlan.card.cancel') }}
      </v-btn>

      <v-btn @click="submit" :loading="isSubmitting" color="primary" elevation="0" large>
        {{ $t('myPlan.card.confirm') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'

import Modal from '@/components/app/Modal'
import { updateCompanyCard } from '@/services/checklist/subscriptionApi'
import {
  name,
  expiredMonthValidation,
  expiredYearValidation,
} from '@/util/errorHandling'

/**
 * Card replacement form.
 *
 * The card lives in this component's local state and nowhere else — not vuex, not localStorage. It is
 * sent once and the fields are wiped as soon as the request settles, whatever the outcome. hcheck
 * forwards it to billing, which tokenizes it with the broker; only the brand and the last four digits
 * ever come back.
 *
 * Masks and validators are the ones the recharge card form already uses, so the two behave the same.
 */
export default {
  name: 'UpdateCompanyCardModal',

  components: { Modal },

  data() {
    return {
      holderName: '',
      cardNumber: '',
      expiry: '',
      ccv: '',
      isSubmitting: false,
      errorMessage: null,

      rules: {
        name,
        expiredMonthValidation,
        expiredYearValidation,
        number: (value) => (!!value && value.replace(/\D/g, '').length >= 13) || this.$t('rules.required'),
        expiry: (value) => (!!value && value.length === 7) || this.$t('rules.required'),
        ccv: (value) => (!!value && value.length >= 3) || this.$t('rules.required'),
      },
    }
  },

  methods: {
    ...mapActions(['showSuccessMessage']),
    ...mapMutations(['closeAllModals']),

    reset() {
      this.holderName = ''
      this.cardNumber = ''
      this.expiry = ''
      this.ccv = ''
      this.errorMessage = null
    },

    close() {
      this.reset()
      this.closeAllModals()
    },

    async submit() {
      if (!this.$refs.cardForm.validate()) return

      this.isSubmitting = true
      this.errorMessage = null

      let data = null

      // Only the request is guarded. Anything after it — the toast, the emit, closing the modal —
      // runs on a card that billing has already replaced, so a throw there must not be reported as a
      // failed save. That mistake is invisible in testing and alarming in production: the customer is
      // told the card was refused while the charge goes to the new one.
      try {
        const response = await updateCompanyCard({
          holderName: this.holderName,
          number: this.cardNumber.replace(/\D/g, ''),
          expiryMonth: this.expiry.split('/')[0],
          expiryYear: this.expiry.split('/')[1],
          ccv: this.ccv,
        })
        data = response.data
      } catch (error) {
        // hcheck answers 200 for every billing state it knows about, so reaching here is a malformed
        // body or a dead request. Either way the card on file is untouched.
        this.errorMessage = this.$t('myPlan.card.error.unavailable')
      } finally {
        // Wiped on every path: a failed attempt is exactly when the fields sit on screen longest.
        this.holderName = ''
        this.cardNumber = ''
        this.expiry = ''
        this.ccv = ''
        this.isSubmitting = false
      }

      if (data) this.handleResult(data)
    },

    handleResult(data) {
      if (data.kind === 'UPDATED') {
        this.showSuccessMessage(this.$t('myPlan.card.success'))
        this.$emit('updated', data.card)
        this.close()
        return
      }

      const messages = {
        REJECTED: this.$t(`myPlan.card.error.${data.reason || 'CARD_REJECTED'}`),
        NOT_READY: this.$t('myPlan.card.error.notReady'),
      }

      this.errorMessage = messages[data.kind] || this.$t('myPlan.card.error.unavailable')
    },
  },
}
</script>
